import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Icon, Segmented } from '@barev/ui';
import { confirmDialog, errorText, formatMoney, haptic, openPayment, useI18n, useToast, useTRPC } from '@barev/web';
import { useEffect, useState } from 'react';
import { LEVEL_NAME, levelDiscount, PageHead, type Level } from '../lib.tsx';

const PLANS = ['BAREV', 'BAREV_PLUS', 'ELITE'] as const;
/** После перехода к оплате ждём вебхук: опрашиваем статус до 5 минут */
const AWAIT_MS = 5 * 60_000;

/**
 * Тарифы клуба как карты на сайте: Barev, Barev+, Barev Elite.
 * Онлайн-оплата через Tribute (€, автопродление каждый месяц); без неё — заявка оператору.
 */
export function Membership() {
  const { t, amd, dt, locale } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [awaitUntil, setAwaitUntil] = useState(0);
  const awaiting = awaitUntil > Date.now();
  const me = useQuery({ ...trpc.client.me.queryOptions(), refetchInterval: awaiting ? 4000 : false });
  const pay = useQuery({ ...trpc.client.pay.info.queryOptions(), refetchInterval: awaiting ? 4000 : false });
  const catalog = useQuery(trpc.client.catalog.queryOptions());
  const open = useQuery(trpc.client.requests.open.queryOptions());
  const current = (me.data?.membership.level ?? 'BAREV') as Level;
  const [plan, setPlan] = useState<Level>(current === 'BAREV' ? 'BAREV_PLUS' : current);
  const request = useMutation(
    trpc.client.requests.create.mutationOptions({
      onSuccess: () => {
        haptic.ok();
        toast(t('client.membership.requested'));
        void qc.invalidateQueries({ queryKey: trpc.client.requests.open.queryKey() });
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );
  const subscribe = useMutation(
    trpc.client.pay.subscribe.mutationOptions({
      onSuccess: (links) => {
        setAwaitUntil(Date.now() + AWAIT_MS);
        openPayment(links);
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );
  const stopRenew = useMutation(
    trpc.client.pay.cancelAutoRenew.mutationOptions({
      onSuccess: () => {
        haptic.ok();
        toast(t('client.membership.renewOff'));
        void qc.invalidateQueries({ queryKey: trpc.client.pay.info.queryKey() });
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );

  const renew = pay.data?.autoRenew ?? null;
  // оплата подтвердилась — перестаём опрашивать
  useEffect(() => {
    if (awaiting && renew) {
      setAwaitUntil(0);
      haptic.ok();
      toast(t('client.membership.paid'));
      void qc.invalidateQueries();
    }
  }, [awaiting, renew, qc, t, toast]);
  // по возвращении из оплаты обновляем сразу
  useEffect(() => {
    const onVisible = () => document.visibilityState === 'visible' && awaiting && void pay.refetch();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [awaiting, pay]);

  const plans = me.data?.plans;
  const online = pay.data?.enabled ? (plan === 'BAREV' ? null : pay.data.plans[plan]) : null;
  const price =
    plan === 'BAREV'
      ? t('landing.club.free')
      : online
        ? t('plans.plus.price', { price: formatMoney(locale, online.cents, 'EUR') })
        : t('plans.plus.price', { price: amd(plan === 'ELITE' ? (plans?.elitePrice ?? 0) : (plans?.plusPrice ?? 0)) });
  const pending = open.data?.find((r) => r.kind === 'SUBSCRIPTION' && r.plan === plan);
  const id = plan === 'BAREV' ? 'free' : plan === 'BAREV_PLUS' ? 'plus' : 'elite';
  const pct = plan === 'ELITE' ? plans?.eliteMaxPct : plans?.plusMaxPct;
  const discounts = (catalog.data?.categories ?? []).filter((c) => c.code !== 'PERKS' && levelDiscount(plan, c) > 0);
  const paidPlan = plan as 'BAREV_PLUS' | 'ELITE';

  async function onStopRenew() {
    if (await confirmDialog(t('client.membership.renewOffConfirm'))) stopRenew.mutate();
  }

  return (
    <main className="app app--plain">
      <PageHead title={t('client.membership.title')} />
      <Segmented value={plan} onChange={setPlan} options={PLANS.map((p) => ({ value: p, label: LEVEL_NAME[p] }))} />

      <div className="club" data-plan={id}>
        <span className="club__top">
          <span>Barev Club</span>
          <span className="club__chip" aria-hidden="true" />
        </span>
        <span className="club__name">{LEVEL_NAME[plan]}</span>
        <span className="club__price">{price}</span>
      </div>
      {online && (
        <p className="bc-small bc-muted" style={{ margin: '-4px 0 0', textAlign: 'center' }}>
          {t('client.membership.approx', { amd: amd(online.amd), rub: formatMoney(locale, online.rub, 'RUB') })}
        </p>
      )}

      <ul className="club-list">
        {[1, 2, 3].map((n) => (
          <li key={n}>
            <Icon name="check" size={18} strokeWidth={2.6} />
            {t(`landing.club.${id}F${n}`, { pct: pct ?? 0 })}
          </li>
        ))}
        {discounts.map((c) => (
          <li key={c.code}>
            <Icon name="check" size={18} strokeWidth={2.6} />
            {t('plans.plus.discount', { pct: levelDiscount(plan, c), category: t(`categories.${c.code}.name`) })}
          </li>
        ))}
      </ul>

      {plan === current && (
        <div className="club-state">
          <b>{t('client.membership.current')}</b>
          {current !== 'BAREV' && me.data?.membership.endsAt && !(renew && renew.plan === plan) && (
            <span>{t('client.home.levelUntil', { plan: LEVEL_NAME[current], date: dt(me.data.membership.endsAt, { day: 'numeric', month: 'long' }) })}</span>
          )}
        </div>
      )}

      {plan === 'BAREV' ? null : renew && renew.plan === plan ? (
        <>
          <div className="club-state">
            <b>{t('client.membership.renewOn')}</b>
            {renew.nextChargeAt && (
              <span>{t('client.membership.nextCharge', { date: dt(renew.nextChargeAt, { day: 'numeric', month: 'long' }), price: formatMoney(locale, renew.cents, 'EUR') })}</span>
            )}
          </div>
          <Button variant="light" block onClick={onStopRenew} disabled={stopRenew.isPending}>
            {t('client.membership.renewOffButton')}
          </Button>
        </>
      ) : renew ? (
        <p className="bc-small bc-muted" style={{ margin: 0, textAlign: 'center' }}>
          {t('client.membership.switchHint', { plan: LEVEL_NAME[renew.plan as Level] })}
        </p>
      ) : awaiting ? (
        <div className="club-state">
          <b>{t('client.membership.awaitingTitle')}</b>
          <span>{t('client.membership.awaitingText')}</span>
        </div>
      ) : online ? (
        <>
          <Button block onClick={() => subscribe.mutate({ plan: paidPlan })} disabled={subscribe.isPending}>
            {t(plan === current ? 'client.membership.payRenew' : 'client.membership.pay', { plan: LEVEL_NAME[plan], price: formatMoney(locale, online.cents, 'EUR') })}
          </Button>
          <p className="bc-small bc-muted" style={{ margin: 0, textAlign: 'center' }}>
            {t('client.membership.payHint')}
          </p>
          {plan !== current &&
            (pending ? (
              <p className="bc-small bc-muted" style={{ margin: 0, textAlign: 'center' }}>{t('client.membership.pendingText')}</p>
            ) : (
              <Button variant="light" size="sm" block onClick={() => request.mutate({ kind: 'SUBSCRIPTION', plan: paidPlan })} disabled={request.isPending}>
                {t('client.membership.payOther')}
              </Button>
            ))}
        </>
      ) : plan === current ? null : pending ? (
        <div className="club-state">
          <b>{t('client.membership.pendingTitle')}</b>
          <span>{t('client.membership.pendingText')}</span>
        </div>
      ) : (
        <>
          <Button onClick={() => request.mutate({ kind: 'SUBSCRIPTION', plan: paidPlan })} disabled={request.isPending}>
            {t('client.membership.request', { plan: LEVEL_NAME[plan] })}
          </Button>
          <p className="bc-small bc-muted" style={{ margin: 0, textAlign: 'center' }}>
            {t('client.membership.requestHint')}
          </p>
        </>
      )}
    </main>
  );
}
